import React from 'react';
import { IconChevronRight } from '@veneer/core';
import { ErrorWidget, useI18n, useRootContext } from '@jarvis/react-portal-addons';
import PropTypes from 'prop-types';
import { publishEvent } from '../../../../utils/analytics';
import { StatusListItem } from '../StatusListItem';
import {
  AddonsWidgetContainer,
  ArrowButton,
  ButtonArea,
  ContentArea,
  Counter,
  ErrorContainer,
  LoaderListContainer,
  LoaderWidget,
  StatusListContainer,
  SubHeader,
  SubHeaderTitle,
} from './styles';

const StatusList = ({
  analyticsEvents: eventAnalytics,
  dataTestId,
  handleRetry,
  items,
  isError,
  isLoading,
  redirectPath,
  subtitle,
  totalCount,
}) => {
  const { shell } = useRootContext();
  const { navigation } = shell;
  const { t } = useI18n();
  const normalizedSubtitle = subtitle.toLowerCase().trim().replace(/[.\s]/g, '-');

  return (
    <ContentArea>
      {isError ? (
        <ErrorContainer>
          <ErrorWidget
            i18n={{
              retry: t('dashboard.error.retry'),
              message: t('dashboard.error.bodyCopy'),
            }}
            onRetry={handleRetry}
            dataTestId={`${dataTestId}-error`}
          />
        </ErrorContainer>
      ) : (
        <StatusListContainer>
          <SubHeader
            onClick={() => {
              publishEvent(eventAnalytics(totalCount));
              navigation?.push?.(redirectPath);
            }}
          >
            <SubHeaderTitle data-testid={`${dataTestId}-${normalizedSubtitle}-subtitle`} className="subtitle-regular">
              {subtitle}
            </SubHeaderTitle>
            <ButtonArea data-testid={`${dataTestId}-${normalizedSubtitle}-counter-area`}>
              {isLoading ? (
                <AddonsWidgetContainer data-testid={`${dataTestId}-${normalizedSubtitle}-loader`}>
                  <LoaderWidget />
                </AddonsWidgetContainer>
              ) : (
                <React.Fragment>
                  <Counter className="body-large bold" data-testid={`${dataTestId}-${normalizedSubtitle}-counter-value`}>
                    {totalCount}
                  </Counter>
                  <ArrowButton data-testid={`${dataTestId}-${normalizedSubtitle}-button`}>
                    <IconChevronRight size={20} color="gray12" />
                  </ArrowButton>
                </React.Fragment>
              )}
            </ButtonArea>
          </SubHeader>
          <div>
            {isLoading ? (
              <LoaderListContainer data-testid={`${dataTestId}-${normalizedSubtitle}-list-loader`}>
                <LoaderWidget />
              </LoaderListContainer>
            ) : (
              <div>
                {items?.map(({ id, ...item }) => (
                  <StatusListItem
                    key={id}
                    dataTestId={dataTestId}
                    id={id}
                    {...item}
                  />
                ))}
              </div>
            )}
          </div>
        </StatusListContainer>
      )}
    </ContentArea>
  );
};

StatusList.propTypes = {
  analyticsEvents: PropTypes.func.isRequired,
  dataTestId: PropTypes.string,
  handleRetry: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    value: PropTypes.number,
    color: PropTypes.string,
    filled: PropTypes.bool,
  })),
  redirectPath: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  isError: PropTypes.bool,
  isLoading: PropTypes.bool,
  totalCount: PropTypes.number,
};

StatusList.defaultProps = {
  dataTestId: '',
  items: [],
  subtitle: '',
  isError: false,
  isLoading: false,
  totalCount: 0,
};

export default StatusList;
